import axios from 'axios'
import { addMonths } from 'date-fns/addMonths'
import { startOfMonth } from 'date-fns/startOfMonth'
import { endOfMonth } from 'date-fns/endOfMonth'
import { parse } from 'date-fns/parse'
import { format } from 'date-fns/format'

import { canBuildRTCalendarRequest, canBuildOWCalendarRequest } from './canBuildRTCalendarRequest.js'
import { calendarQuery } from './calendarQuery'

const validCabinClass = ['ECO', 'PEC', 'BUS', 'FST']

const ORIGIN = 'origin'
const DESTINATION = 'destination'

export function buildOWPricedCalendarRequest(tripDetails, displayMonths) {
  if (!canBuildOWCalendarRequest(tripDetails, displayMonths)) return null

  const { displayedFromMonth, displayedToMonth } = displayMonths
  const { flights, cabinClass } = tripDetails

  const fromDate = getStartOfMonth(displayedFromMonth)
  const toDate = getEndOfMonth(displayedToMonth)

  const trips = flights.map((flight) => {
    const originLocation = getLocationCode(flight.departureAirport, ORIGIN)
    const destinationLocation = getLocationCode(flight.arrivalAirport, DESTINATION)

    const trip = { ...originLocation, ...destinationLocation }

    trip.departDateRange = {
      fromDate,
      toDate,
    }

    return trip
  })

  const request = {
    trips,
    size: 720,
    consumer: 'PCLN-HOME',
  }

  if (validCabinClass.includes(cabinClass)) {
    request.cabinClass = cabinClass
  }
  return request
}

export function getLocationCode(location, type) {
  const locationCode = {}
  if (location.id === location.cityCode) {
    locationCode[`${type}City`] = [location.id]
  } else {
    locationCode[`${type}`] = [location.id]
  }
  return locationCode
}

export function getStartOfMonth(displayedFromMonth) {
  const today = new Date()
  const fromMonth = startOfMonth(parse(displayedFromMonth, 'yyyyMM', today))
  return format(today.valueOf() > fromMonth.valueOf() ? today : fromMonth, 'yyyy-MM-dd')
}

export function getEndOfMonth(displayedToMonth) {
  return format(endOfMonth(parse(displayedToMonth, 'yyyyMM', new Date())), 'yyyy-MM-dd')
}

export function pricePromise(request, getGraphURL, apolloClientInfo) {
  return new Promise((resolve, reject) => {
    const data = {
      operationName: 'airPriceGuideCalendar',
      variables: { input: request },
      query: calendarQuery,
    }

    const url = getGraphURL?.({ operationName: data.operationName }) || '/pws/v0/pcln-graph/'

    axios({
      url,
      method: 'post',
      data,
      headers: {
        'apollographql-client-name': apolloClientInfo?.clientName,
        'apollographql-client-version': apolloClientInfo?.clientVersion,
      },
    })
      .then((searchResult) => {
        if (searchResult?.data?.data?.airPriceGuide?.records?.length) {
          resolve(searchResult.data.data.airPriceGuide.records)
        }

        resolve([])
      })
      .catch((error) => {
        // The API and FLY QL are already logging in case of any error
        // if an error occurs, the price map in calendar is cleared
        reject(error)
      })
  })
}

export function processRecords(apiRecords, doubleCalendarPricing = false) {
  let dayMap = {}
  apiRecords.forEach((apiRecord) => {
    dayMap = apiRecord.reduce((total, record) => {
      if (
        record.dates &&
        record.dates[0] &&
        record.minimumFare &&
        record.minimumFare.currency === 'USD' &&
        record.minimumFare.amtPerPax > 0
      ) {
        const priceDate = record.dates[1] || record.dates[0]
        const dateKey = priceDate.replace(/-/g, '')
        const price = doubleCalendarPricing
          ? Math.ceil(record.minimumFare.amtPerPax) * 2
          : Math.ceil(record.minimumFare.amtPerPax)
        if (!total[dateKey] || total[dateKey].price > price) {
          total[dateKey] = {
            price,
          }
        }
      }
      return total
    }, dayMap)
  })
  return dayMap
}

export function buildRTPricedCalendarRequest(tripDetails, displayMonths, departDate) {
  if (!canBuildRTCalendarRequest(tripDetails, displayMonths, departDate)) return null

  const { displayedFromMonth, displayedToMonth } = displayMonths
  const { flights, cabinClass } = tripDetails
  const departureDate = departDate || flights[0].departureDate

  const fromDate = getStartOfMonth(displayedFromMonth)
  const toDate = getEndOfMonth(displayedToMonth)

  const trips = flights.reduce((tripsRequest, flight) => {
    const departOriginLocation = getLocationCode(flight.departureAirport, ORIGIN)
    const departDestinationLocation = getLocationCode(flight.arrivalAirport, DESTINATION)
    const returnOriginLocation = getLocationCode(flight.arrivalAirport, ORIGIN)
    const returnDestinationLocation = getLocationCode(flight.departureAirport, DESTINATION)

    return [
      {
        ...departOriginLocation,
        ...departDestinationLocation,
        departDate: normalizeDate(departureDate),
      },
      {
        ...returnOriginLocation,
        ...returnDestinationLocation,
        departDateRange: {
          fromDate,
          toDate,
        },
      },
    ]
  }, [])

  const request = {
    trips,
    size: 720,
    consumer: 'PCLN-HOME',
  }

  if (validCabinClass.includes(cabinClass)) {
    request.cabinClass = cabinClass
  }
  return request
}

export function normalizeDate(date) {
  if (!date) {
    return ''
  }
  return date.substr(0, 4) + '-' + date.substr(4, 2) + '-' + date.substr(6, 2)
}

/**
 BELOW ARE FUNCTIONS EXTRACTED FROM AIRPRICEDCALENDAR COMPONENT
 */

export function shouldRequestMonthFromApi(displayMonths, executedMonths) {
  const { displayedFromMonth, displayedToMonth } = displayMonths
  let apiCallRequired = false

  if (displayedToMonth && displayedFromMonth) {
    let fromMonth = parse(displayedFromMonth, 'yyyyMM', new Date())
    const toMonth = parse(displayedToMonth, 'yyyyMM', new Date())
    while (fromMonth.valueOf() <= toMonth.valueOf()) {
      const fromMonthKey = format(fromMonth, 'yyyyMM')
      if (!executedMonths[fromMonthKey]) {
        executedMonths[fromMonthKey] = true
        apiCallRequired = true
      }
      fromMonth = addMonths(fromMonth, 1)
    }
  }

  return apiCallRequired
}
